'use client';

import React, { useState } from 'react';
import { ShieldCheck, Search, AlertTriangle, User, Calendar, Download, Plus,  } from 'lucide-react';
import { DD_REVIEWS, type DDReview } from '@/lib/mockData';
import StatusBadge from '@/components/ui/StatusBadge';
import { toast } from 'sonner';
import DDReviewPanel from './DDReviewPanel';

// Backend integration point: replace with /api/due-diligence/reviews
export default function DDWorkspace() {
  const [selectedReview, setSelectedReview] = useState<DDReview | null>(DD_REVIEWS[0]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  const filtered = DD_REVIEWS.filter((r) => {
    const q = searchQuery.toLowerCase();
    const matchQ = !q || r.vendorName.toLowerCase().includes(q) || r.domain.toLowerCase().includes(q);
    const matchStatus = !filterStatus || r.status === filterStatus;
    return matchQ && matchStatus;
  });

  const statusCounts = {
    total: DD_REVIEWS.length,
    inProgress: DD_REVIEWS.filter((r) => r.status === 'In Progress').length,
    overdue: DD_REVIEWS.filter((r) => r.status === 'Overdue').length,
    pendingApproval: DD_REVIEWS.filter((r) => r.status === 'Pending Approval').length,
    notStarted: DD_REVIEWS.filter((r) => r.status === 'Not Started').length,
  };

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Page header */}
      <div className="px-5 py-3 border-b border-[hsl(var(--border))] bg-[hsl(var(--surface))] flex items-center justify-between gap-3 flex-shrink-0">
        <div>
          <h1 className="text-xl font-bold text-[hsl(var(--text-primary))]">Due Diligence Workspace</h1>
          <p className="text-xs text-[hsl(var(--text-muted))] mt-0.5">
            {statusCounts.inProgress} in progress · {statusCounts.overdue} overdue · {statusCounts.pendingApproval} pending approval
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => toast.success('Export started', { description: 'DD status report being prepared.' })}
            className="btn-secondary"
          >
            <Download size={13} />
            Export Report
          </button>
          <button
            onClick={() => toast.info('Assign review coming soon.')}
            className="btn-primary"
          >
            <Plus size={13} />
            Assign Review
          </button>
        </div>
      </div>

      {/* Summary strip */}
      <div className="flex border-b border-[hsl(var(--border))] bg-[hsl(var(--background))] flex-shrink-0 divide-x divide-[hsl(var(--border))]">
        {[
          { label: 'Total Reviews', value: statusCounts.total, color: 'text-[hsl(var(--text-primary))]' },
          { label: 'In Progress', value: statusCounts.inProgress, color: 'text-blue-600 dark:text-blue-400' },
          { label: 'Overdue', value: statusCounts.overdue, color: 'text-red-600 dark:text-red-400' },
          { label: 'Pending Approval', value: statusCounts.pendingApproval, color: 'text-amber-600 dark:text-amber-400' },
          { label: 'Not Started', value: statusCounts.notStarted, color: 'text-[hsl(var(--text-muted))]' },
        ].map((s) => (
          <div key={`dd-sum-${s.label}`} className="flex-1 px-4 py-2.5 text-center">
            <p className={`text-lg font-bold tabular-nums ${s.color}`}>{s.value}</p>
            <p className="text-2xs text-[hsl(var(--text-muted))] mt-0.5">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Main split layout */}
      <div className="flex-1 flex min-h-0 overflow-hidden">
        {/* Left: review queue */}
        <div className="w-72 xl:w-80 flex-shrink-0 border-r border-[hsl(var(--border))] flex flex-col min-h-0 bg-[hsl(var(--surface))]">
          {/* Queue filters */}
          <div className="p-3 border-b border-[hsl(var(--border))] space-y-2 flex-shrink-0">
            <div className="relative">
              <Search
                size={12}
                className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[hsl(var(--text-muted))]"
              />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search reviews…"
                className="input-base pl-8 text-xs h-7 w-full"
              />
            </div>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="input-base text-xs h-7 w-full cursor-pointer"
            >
              <option value="">All Statuses</option>
              <option value="In Progress">In Progress</option>
              <option value="Overdue">Overdue</option>
              <option value="Pending Approval">Pending Approval</option>
              <option value="Not Started">Not Started</option>
              <option value="Complete">Complete</option>
            </select>
          </div>

          {/* Review list */}
          <div className="flex-1 overflow-y-auto divide-y divide-[hsl(var(--border))]">
            {filtered.map((review) => {
              const isSelected = selectedReview?.id === review.id;
              return (
                <button
                  key={review.id}
                  onClick={() => setSelectedReview(review)}
                  className={`w-full text-left px-3 py-3 transition-colors hover:bg-[hsl(var(--background))] ${
                    isSelected
                      ? 'bg-teal-50 dark:bg-teal-950/20 border-r-2 border-teal-600' :''
                  }`}
                >
                  <div className="flex items-start justify-between gap-1.5 mb-1">
                    <span className="text-xs font-medium text-[hsl(var(--text-primary))] leading-snug">
                      {review.vendorName}
                    </span>
                    <StatusBadge type="risk" value={review.riskTier} className="flex-shrink-0 text-2xs" />
                  </div>
                  <div className="flex items-center justify-between gap-1.5 mb-1.5">
                    <span className="text-2xs font-medium text-[hsl(var(--text-secondary))]">
                      {review.domain}
                    </span>
                    <StatusBadge type="dd" value={review.status} className="flex-shrink-0 text-2xs" />
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1 text-2xs text-[hsl(var(--text-muted))]">
                      <User size={10} />
                      <span>{review.reviewer}</span>
                    </div>
                    <div className="flex items-center gap-1 text-2xs text-[hsl(var(--text-muted))]">
                      <Calendar size={10} />
                      <span>{review.dueDate}</span>
                    </div>
                  </div>
                  {/* Progress bar */}
                  <div className="mt-2">
                    <div className="flex items-center justify-between text-2xs text-[hsl(var(--text-muted))] mb-1">
                      <span>Completion</span>
                      <span className="tabular-nums">{review.completionPct}%</span>
                    </div>
                    <div className="h-1 rounded-full bg-[hsl(var(--border))]">
                      <div
                        className={`h-1 rounded-full transition-all ${
                          review.status === 'Overdue' ?'bg-red-500'
                            : review.completionPct === 100
                            ? 'bg-emerald-500' :'bg-teal-500'
                        }`}
                        style={{ width: `${review.completionPct}%` }}
                      />
                    </div>
                  </div>
                  {review.findingsCount > 0 && (
                    <div className="mt-1.5 flex items-center gap-1 text-2xs text-amber-600 dark:text-amber-400">
                      <AlertTriangle size={10} />
                      <span>{review.findingsCount} finding{review.findingsCount > 1 ? 's' : ''}</span>
                    </div>
                  )}
                </button>
              );
            })}
            {filtered.length === 0 && (
              <div className="py-10 text-center px-4">
                <ShieldCheck size={24} className="mx-auto text-[hsl(var(--text-muted))] opacity-40 mb-2" />
                <p className="text-xs text-[hsl(var(--text-muted))]">No reviews match your filters.</p>
              </div>
            )}
          </div>
        </div>

        {/* Right: review detail panel */}
        <div className="flex-1 overflow-hidden">
          {selectedReview ? (
            <DDReviewPanel review={selectedReview} />
          ) : (
            <div className="h-full flex items-center justify-center">
              <div className="text-center">
                <ShieldCheck size={32} className="mx-auto text-[hsl(var(--text-muted))] opacity-30 mb-3" />
                <p className="text-sm font-medium text-[hsl(var(--text-secondary))]">
                  Select a review from the queue
                </p>
                <p className="text-xs text-[hsl(var(--text-muted))] mt-1">
                  Choose a due diligence review to view its workspace.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}